import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Shared layout primitives
// ---------------------------------------------------------------------------

const PAGE = {
  minHeight: '100vh',
  background: '#1a1a2e',
  display: 'flex',
  alignItems: 'flex-start',   // allow scrolling on small screens
  justifyContent: 'center',
  padding: '2rem 1rem 3rem',
};

const CARD = {
  background: '#fff',
  borderRadius: '12px',
  padding: '2.5rem 2rem',
  width: '100%',
  maxWidth: '480px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
};

function BrandHeader() {
  return (
    <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e', letterSpacing: '-0.3px' }}>
        <span style={{ color: '#d4af37' }}>SCALA</span> Song Competition
      </div>
      <div style={{
        fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem',
        letterSpacing: '0.06em', textTransform: 'uppercase',
      }}>
        FOOM Live — Heat Confirmation
      </div>
    </div>
  );
}

/** Simple centred status card for terminal states. */
function StatusCard({ icon, heading, body }) {
  return (
    <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{icon}</div>
      {heading && (
        <p style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: '0.5rem', fontSize: '1rem' }}>
          {heading}
        </p>
      )}
      {body && (
        <p style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
          {body}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function HeatConfirm() {
  const { token } = useParams();

  // ── Fetch state ──────────────────────────────────────────────────────────
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState(null);   // null | 'not_found' | 'expired' | 'server_error'
  const [data,       setData]       = useState(null);   // valid API payload

  // ── Action state ─────────────────────────────────────────────────────────
  // Performers can no longer propose an alternative date from this page — the
  // expectation is that they contact FOOM directly instead. 'alt_requested'
  // remains only as a state legacy rows can still be in.
  const [done,         setDone]         = useState(null);  // null | 'confirmed'
  const [submitting,   setSubmitting]   = useState(false);
  const [confirmError, setConfirmError] = useState('');

  // ── Validate token on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setFetchError('not_found');
      setLoading(false);
      return;
    }
    api.get(`/api/heat-confirm/${token}`)
      .then((res) => {
        const d = res.data;
        if (!d.valid) {
          setFetchError(d.reason || 'not_found');
        } else {
          setData(d);
        }
        setLoading(false);
      })
      .catch(() => {
        setFetchError('not_found');
        setLoading(false);
      });
  }, [token]);

  // ── Confirm attendance ────────────────────────────────────────────────────
  async function handleConfirm() {
    setConfirmError('');
    setSubmitting(true);
    try {
      await api.post(`/api/heat-confirm/${token}/confirm`);
      setDone('confirmed');
    } catch (err) {
      setConfirmError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // Loading
  if (loading) {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <BrandHeader />
          <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#6b7280' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
            Loading…
          </div>
        </div>
      </div>
    );
  }

  // Allocation changed since this link was sent — the song moved heats, was
  // unassigned or withdrawn. Says so plainly rather than "link not valid",
  // which would read as a fault and prompt a support email.
  if (fetchError === 'moved') {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <BrandHeader />
          <StatusCard
            icon="🔄"
            heading="Your allocation has changed"
            body="This link is out of date because your heat allocation has changed. Please use the link in the most recent email we sent you, or contact us at scalafoom@gmail.com if you cannot find it."
          />
        </div>
      </div>
    );
  }

  // Token not found / server error
  if (fetchError && fetchError !== 'expired') {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <BrandHeader />
          <StatusCard
            icon="⚠️"
            heading="Link not valid"
            body="This link is not valid or has already expired. Please use the link from your notification email, or contact us for help."
          />
        </div>
      </div>
    );
  }

  // Token expired (heat date passed)
  if (fetchError === 'expired') {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <BrandHeader />
          <StatusCard
            icon="⏰"
            heading="Link expired"
            body="This link has expired — the heat date has passed. Please contact us if you have any questions."
          />
        </div>
      </div>
    );
  }

  // Already confirmed (returned by GET, before any action this session)
  if (data?.already_confirmed) {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <BrandHeader />
          <StatusCard
            icon="✅"
            heading="Already confirmed"
            body={`You have already confirmed your participation in ${data.heat.heat_name}. See you there!`}
          />
        </div>
      </div>
    );
  }

  // Already requested alt date (returned by GET)
  if (data?.already_requested_alt) {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <BrandHeader />
          <StatusCard
            icon="📋"
            heading="Request received"
            body="We have received your request for an alternative heat date. We will be in touch shortly."
          />
        </div>
      </div>
    );
  }

  // Post-confirm success (set after successful POST this session)
  if (done === 'confirmed') {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <BrandHeader />
          <StatusCard
            icon="✅"
            heading={`Thank you, ${data.firstname}!`}
            body={`Your participation in ${data.heat.heat_name} is confirmed. We look forward to seeing you on ${data.heat.heat_date_formatted}.`}
          />
        </div>
      </div>
    );
  }

  // ── Main confirmation page ─────────────────────────────────────────────────
  const { heat, firstname } = data;

  const detailRows = [
    ['Heat',         heat.heat_name],
    ['Date',         heat.heat_date_formatted],
    ['Arrival',      heat.arrival_time  || '—'],
    ['Venue',        heat.venue_name    || '—'],
    ['Address',      heat.venue_address || '—'],
  ];

  return (
    <div style={PAGE}>
      <div style={CARD}>
        <BrandHeader />

        {/* Greeting */}
        <p style={{ color: '#374151', fontSize: '0.95rem', marginBottom: '0.25rem', margin: '0 0 0.25rem' }}>
          Hi <strong>{firstname}</strong>,
        </p>
        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
          Here are the details for your upcoming heat:
        </p>

        {/* Heat details */}
        <div style={{
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          marginBottom: '1.75rem',
        }}>
          {detailRows.map(([label, value], idx) => (
            <div
              key={label}
              style={{
                display: 'flex',
                gap: '0.75rem',
                padding: '0.6rem 1rem',
                borderBottom: idx < detailRows.length - 1 ? '1px solid #e5e7eb' : 'none',
              }}
            >
              <span style={{
                flexShrink: 0,
                width: '72px',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                paddingTop: '2px',
              }}>
                {label}
              </span>
              <span style={{ fontSize: '0.9rem', color: '#1a1a2e', fontWeight: 500, lineHeight: 1.4 }}>
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* Confirm button */}
        {confirmError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
            padding: '0.65rem 0.9rem', color: '#dc2626', fontSize: '0.85rem',
            marginBottom: '0.75rem',
          }}>
            {confirmError}
          </div>
        )}
        <button
          onClick={handleConfirm}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '0.9rem 1rem',
            background: submitting ? '#9ca3af' : '#1a1a2e',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: submitting ? 'default' : 'pointer',
            letterSpacing: '0.2px',
            marginBottom: '1.5rem',
          }}
        >
          {submitting ? 'Submitting…' : '✓  Confirm My Participation'}
        </button>

        <p style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.6, margin: 0, textAlign: 'center' }}>
          If this date does not suit, please get in touch with us at{' '}
          <a href="mailto:scalafoom@gmail.com" style={{ color: '#1a1a2e', fontWeight: 600 }}>
            scalafoom@gmail.com
          </a>.
        </p>
      </div>
    </div>
  );
}
